export class ScrollMotionController {
  constructor() {
    this.sections = new Set();
    this.active = new Set();
    this.subscribers = new Set();
    this.frame = 0;
    this.scroll = 0;
    this.schedule = () => {
      if (!this.frame && this.subscribers.size && !document.hidden)
        this.frame = requestAnimationFrame((time) => this.update(time));
    };
    this.onScroll = () => {
      this.scroll = window.scrollY;
      this.schedule();
    };
    this.refresh = () => {
      for (const section of this.sections) {
        const running = String(
          this.active.has(section) &&
            !this.reduced.matches &&
            !document.hidden &&
            !section.hasAttribute('data-motion-paused'),
        );
        if (section.dataset.motionRunning !== running) section.dataset.motionRunning = running;
      }
      if (document.hidden) {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
      } else this.onScroll();
    };
  }
  start() {
    if (this.events && !this.events.signal.aborted) return;
    this.scroll = window.scrollY;
    this.events = new AbortController();
    const { signal } = this.events;
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.reduced.addEventListener('change', this.refresh, { signal });
    window.addEventListener('scroll', this.onScroll, { passive: true, signal });
    window.addEventListener('pageshow', this.refresh, { signal });
    document.addEventListener('visibilitychange', this.refresh, { signal });
    this.intersections = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!this.sections.has(entry.target)) continue;
          if (entry.isIntersecting) this.active.add(entry.target);
          else this.active.delete(entry.target);
        }
        this.refresh();
      },
      { rootMargin: '0px' },
    );
  }
  register(section) {
    if (this.sections.has(section)) return;
    this.start();
    this.sections.add(section);
    this.intersections.observe(section);
  }
  unregister(section) {
    this.sections.delete(section);
    this.active.delete(section);
    this.intersections?.unobserve(section);
    this.stopIfUnused();
  }
  subscribe(callback) {
    this.start();
    this.subscribers.add(callback);
    this.schedule();
    return () => {
      this.subscribers.delete(callback);
      this.stopIfUnused();
    };
  }
  stopIfUnused() {
    if (this.subscribers.size) return;
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    if (this.sections.size) return;
    this.events?.abort();
    this.intersections?.disconnect();
  }
  update(time) {
    this.frame = 0;
    if (document.hidden) return;
    let pending = false;
    for (const callback of this.subscribers)
      pending = callback(this.scroll, this.reduced.matches, time) === true || pending;
    if (pending) this.schedule();
  }
}
export const scrollMotion = new ScrollMotionController();
// A shopper's pause holds for every banner during this visit (WCAG 2.2.2).
const pauseKey = 'meri-motion-paused';
function readPaused() {
  try {
    return sessionStorage.getItem(pauseKey) === '1';
  } catch {
    return false;
  }
}
class MeriScrollMotion extends HTMLElement {
  connectedCallback() {
    const copy = this.querySelector('.motion-banner__copy');
    const decoration = this.querySelector('.motion-banner__decoration');
    if (copy) {
      // Reserve only the space the actual mobile content and overlay header need.
      this.layoutSize = new ResizeObserver(() => {
        if (this.layoutFrame) return;
        // Measuring may change the banner height; leave the observer delivery first.
        this.layoutFrame = requestAnimationFrame(() => {
          this.layoutFrame = 0;
          if (this.isConnected) this.measureMobileLayout();
        });
      });
      this.layoutSize.observe(copy);
      if (decoration) this.layoutSize.observe(decoration);
      this.layoutEvents = new AbortController();
      for (const name of ['shopify:section:load', 'shopify:section:reorder', 'shopify:section:unload'])
        document.addEventListener(
          name,
          (event) => this.measureMobileLayout(name === 'shopify:section:unload' ? event.target : null),
          { signal: this.layoutEvents.signal },
        );
      this.measureMobileLayout();
    }
    const track = this.querySelector('.motion-banner__track');
    const group = track?.querySelector('.motion-banner__group');
    if (group && this.dataset.motion !== 'off') {
      // Measure only when the text's size changes, never on animation frames.
      this.textSize = new ResizeObserver(([entry]) => {
        const box = entry.borderBoxSize?.[0];
        if (!box) return;
        const distance = box.inlineSize;
        // Large display type needs smaller per-frame steps even at the fastest preset.
        const speed = { gentle: 20, standard: 32, pronounced: 48 }[this.dataset.motion] || 20;
        const duration = (distance / speed) * 1000;
        if (!duration || duration === this.travelDuration) return;
        const animation = track.getAnimations()[0];
        const oldDuration = animation?.effect.getTiming().duration;
        const progress = oldDuration > 0 ? (animation.currentTime || 0) / oldDuration : 0;
        this.travelDuration = duration;
        track.style.setProperty('--motion-duration', `${duration}ms`);
        // Changing duration must not jump to a different point in the loop.
        if (animation) animation.currentTime = progress * duration;
      });
      this.textSize.observe(group, { box: 'border-box' });
    }
    const font = this.dataset.motionFont;
    if (font && document.fonts) {
      const generation = (this.fontGeneration || 0) + 1;
      this.fontGeneration = generation;
      const text = this.querySelector('.motion-banner__group')?.textContent.trim() || '';
      this.dataset.motionFontReady = String(document.fonts.check(font, text));
      if (this.dataset.motionFontReady === 'false') {
        const ready = () => {
          if (this.isConnected && this.fontGeneration === generation) this.dataset.motionFontReady = 'true';
        };
        document.fonts.load(font, text).then(ready, ready);
      }
    }
    const toggle = this.querySelector('[data-motion-toggle]');
    if (toggle && this.dataset.motion !== 'off') {
      this.toggleEvents?.abort();
      this.toggleEvents = new AbortController();
      this.setPaused(readPaused(), toggle);
      toggle.addEventListener(
        'click',
        () => {
          const paused = !this.hasAttribute('data-motion-paused');
          try {
            if (paused) sessionStorage.setItem(pauseKey, '1');
            else sessionStorage.removeItem(pauseKey);
          } catch {}
          for (const section of scrollMotion.sections) {
            const button = section.querySelector('[data-motion-toggle]');
            if (button) section.setPaused(paused, button);
          }
          scrollMotion.refresh();
        },
        { signal: this.toggleEvents.signal },
      );
    }
    if (this.dataset.motion !== 'off') scrollMotion.register(this);
  }
  measureMobileLayout(excluded) {
    const header = [...document.querySelectorAll('.shopify-section-group-header-group > .site-header')].find(
      (element) => !excluded?.contains(element),
    );
    if (header !== this.layoutHeader) {
      if (this.layoutHeader) this.layoutSize.unobserve(this.layoutHeader);
      if (header) this.layoutSize.observe(header);
      this.layoutHeader = header;
    }
    const first = [...(document.querySelector('#MainContent')?.children || [])].find(
      (element) => !excluded?.contains(element),
    );
    const overlaysHeader = first === this.parentElement && !this.classList.contains('motion-banner--video');
    const safeTop = overlaysHeader && header ? header.offsetHeight + 12 : 0;
    const copy = this.querySelector('.motion-banner__copy').offsetHeight;
    const decoration = this.querySelector('.motion-banner__decoration')?.offsetHeight || 0;
    this.style.setProperty('--motion-header-safe', `${safeTop}px`);
    this.style.setProperty('--motion-copy-size', `${copy}px`);
    this.style.setProperty('--motion-decoration-size', `${decoration}px`);
    this.style.setProperty('--motion-mobile-min-height', `${safeTop + (safeTop ? decoration + 12 : 0) + copy + 6}px`);
  }
  setPaused(paused, toggle) {
    this.toggleAttribute('data-motion-paused', paused);
    toggle.dataset.playing = String(!paused);
    toggle.setAttribute('aria-label', paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
  }
  disconnectedCallback() {
    this.layoutEvents?.abort();
    if (this.layoutSize) {
      this.layoutSize.disconnect();
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = 0;
      this.layoutSize = null;
      this.layoutHeader = null;
      for (const name of ['header-safe', 'copy-size', 'decoration-size', 'mobile-min-height'])
        this.style.removeProperty(`--motion-${name}`);
    }
    this.toggleEvents?.abort();
    this.toggleEvents = null;
    this.textSize?.disconnect();
    this.textSize = null;
    this.travelDuration = 0;
    this.fontGeneration = (this.fontGeneration || 0) + 1;
    scrollMotion.unregister(this);
    this.removeAttribute('data-motion-running');
  }
}
if (!customElements.get('meri-scroll-motion')) customElements.define('meri-scroll-motion', MeriScrollMotion);
